import { useDemandClient } from '../../agency/useDemandClient';
import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { createDemandClient } from '../../agency/demandApi';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createBrandsClient } from './api';
import type { BrandPayload, BrandsFilter } from './model';

export const brandsQueryKey = ['campaigns', 'brands'] as const;
export const brandsListsQueryKey = [...brandsQueryKey, 'list'] as const;

function useBrandsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const scope = useAgencyCampaignContext();
  if (!session) throw new Error('Shigo session is required.');
  const referenceClient = createBrandsClient({
    config: { apiBaseUrl: config.madSenseServiceUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
  if (scope) {
    const client = createDemandClient({
      config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
      accessToken: session.accessToken,
      agencyId: scope.agency.id,
    }).brands;
    return {
      ...client,
      listIndustries: () => referenceClient.listIndustries(),
      listCountries: () => referenceClient.listCountries(),
      create: (payload: BrandPayload, logo?: File | null) =>
        client.create(scope.agency.id, payload, logo),
    };
  }
  return referenceClient;
}

export function useBrandsQuery(filter: BrandsFilter, enabled = true) {
  const client = useBrandsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    enabled: enabled && !filter.agencyIds?.length,
    queryKey: [...prefix, ...brandsListsQueryKey, filter],
    queryFn: filter.agencyIds?.length
      ? skipToken
      : () =>
          filter.brandIds?.length === 0
            ? Promise.resolve({ content: [], totalElements: 0, totalPages: 0, number: filter.page })
            : client.list(filter),
  });
}

export function useBrandQuery(id: string) {
  const client = useBrandsClient();
  const adminClient = useDemandClient();
  const scope = useAgencyCampaignContext();
  const isAdmin = !scope;
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    queryKey: [...prefix, ...brandsQueryKey, 'detail', id],
    queryFn: () => (isAdmin ? adminClient.brands.get(id) : client.get(id)),
    enabled: Boolean(id),
  });
}

export function useIndustriesQuery(enabled = true) {
  const client = useBrandsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    queryKey: [...prefix, ...brandsQueryKey, 'industries'],
    enabled,
    queryFn: () => client.listIndustries(),
  });
}

export function useCountriesQuery(enabled = true) {
  const client = useBrandsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    queryKey: [...prefix, ...brandsQueryKey, 'countries'],
    enabled,
    queryFn: () => client.listCountries(),
  });
}

export function useBrandMutations() {
  const client = useBrandsClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  const queryClient = useQueryClient();
  const invalidateLists = () => {
    const refresh = queryClient.invalidateQueries({
      queryKey: [...prefix, ...brandsListsQueryKey],
    });
    if (!scope) return refresh;
    void refresh;
  };
  return {
    create: useMutation({
      mutationFn: ({ payload, logo }: { payload: BrandPayload; logo?: File | null }) =>
        client.create(payload, logo),
      onSuccess: invalidateLists,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        payload,
        logo,
      }: {
        id: string;
        payload: BrandPayload;
        logo?: File | null;
      }) => client.update(id, payload, logo),
      onSuccess: invalidateLists,
    }),
  };
}
